// Settings → Bank data (Financials v2, slice 1): where the Bank vs QuickBooks
// figures come from. Reads bank_status (mapped accounts, recent uploads) and
// get_accounts (the mapping picker) through the proxy; writes only through
// the bankUploadStatement and bankSetAccount callables. No client Firestore.
//
// The upload flow is component state:
//   idle → reading → uploading → done
//                              ↘ mapping (needsAccount) → saving → storing → done
// An error lands inline where it happened (under the button, or under the
// prompt, which stays open for another try). The file's base64 is held only
// while a prompt needs it; nothing here logs it or the unlock token.
// Markup: BankParts.jsx.
import React, { useCallback, useRef, useState } from "react";
import { C, btnP } from "../../../lib/styles.js";
import notify from "../../../lib/notify.jsx";
import { bankSetAccount, bankUploadStatement, isLockRequired } from "../../../lib/qboClient.js";
import { useQbo } from "../lib/useQbo.js";
import { accountsForKind, acctName, defaultLabel, fileToBase64, mappedForFile, sourceLine } from "../lib/bankData.js";
import {
  FetchError, MappedAccounts, MappingPrompt, RecentUploadsTable, RemapForm, UploadResult,
} from "./BankParts.jsx";
import { Kv, PanelHd, PanelNote, lblStyle, panel } from "./common.jsx";

const IDLE = { step: "idle" };
// Errors whose message is written for the person: shown as the server wrote it.
const SAID_INLINE = new Set(["functions/already-exists", "functions/invalid-argument", "functions/not-found"]);
const UPLOAD_FAILED = "The file could not be stored. Try again: re-uploading the same file is safe.";
const SAVE_FAILED = "The mapping could not be saved. Try again.";
const FORMAT_LINE = "QFX (Bank of America's “WEB Connect for QuickBooks”) or CSV";
const KEPT_LINE = "Server-side only, behind this gate · 13 months";

export default function BankDataCard({ ctx, qbo, onChanged }) {
  const status = useQbo(ctx, "bank_status");
  const qboAccts = useQbo(ctx, "get_accounts");
  const connected = qbo?.connected === true;
  const inputRef = useRef(null);
  // Flow generation: a response for a flow that was cancelled or replaced is dropped.
  const gen = useRef(0);
  const [flow, setFlow] = useState(IDLE);
  const [edit, setEdit] = useState(null); // { accountId, label, qboAccountId, busy, error }

  const accounts = status.data?.accounts ?? [];
  const unlockToken = ctx.unlockToken;
  const refreshStatus = status.refresh;

  // After a write: empty the shared cache (Overview and Expenses re-read) and re-read this card.
  const changed = useCallback(() => {
    onChanged?.();
    refreshStatus();
  }, [onChanged, refreshStatus]);

  /** Lock → relock the app; else the message to show inline (and a toast for the unexpected). */
  const failure = (e, generic) => {
    if (isLockRequired(e)) {
      ctx.onLockRequired?.();
      return null;
    }
    if (SAID_INLINE.has(e?.code) && e.message) return e.message;
    notify.error(e?.message || generic);
    return generic;
  };

  const upload = (fileName, base64, accountId) => bankUploadStatement({
    unlockToken, fileName, contentBase64: base64, ...(accountId ? { accountId } : {}),
  });

  /** An upload's answer: a prompt when it needs an account, else the result. */
  const land = (res, fileName, base64) => {
    if (res?.needsAccount) {
      const s = res.summary ?? {};
      setFlow({
        step: "mapping", fileName, base64, reason: res.reason, summary: s,
        label: res.reason === "unmapped" ? defaultLabel(s.kind, s.last4) : "",
        qboAccountId: "", accountId: "", busy: null, error: null,
      });
      return;
    }
    if (res?.ok !== true) {
      notify.error("The upload returned no result");
      setFlow({ step: "idle", error: UPLOAD_FAILED });
      return;
    }
    setFlow({ step: "done", fileName, result: res });
    changed();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // the same file can be picked again
    if (!file) return;
    const id = ++gen.current;
    const fileName = String(file.name || "statement").slice(0, 200); // the callable's limit
    setEdit(null);
    setFlow({ step: "reading", fileName });
    let base64;
    try {
      base64 = await fileToBase64(file);
    } catch (err) {
      if (gen.current === id) setFlow({ step: "idle", error: err.message });
      return;
    }
    if (gen.current !== id) return;
    setFlow({ step: "uploading", fileName });
    try {
      const res = await upload(fileName, base64);
      if (gen.current === id) land(res, fileName, base64);
    } catch (err) {
      if (gen.current !== id) return;
      const msg = failure(err, UPLOAD_FAILED);
      if (msg) setFlow({ step: "idle", error: msg });
    }
  };

  // ── The prompt ────────────────────────────────────────────────────────────
  const kind = flow.summary?.kind;
  const qboChoices = kind ? accountsForKind(qboAccts.data?.accounts, kind) : [];
  const patch = (p) => setFlow((f) => (f.step === "mapping" ? { ...f, ...p } : f));

  const saveAndStore = async () => {
    const f = flow;
    const picked = qboChoices.find((a) => a.id === f.qboAccountId);
    const label = (f.label ?? "").trim();
    if (f.step !== "mapping" || f.busy || !picked || !label || !f.summary?.last4) return;
    const id = gen.current;
    setFlow({ ...f, busy: "saving", error: null });
    try {
      await bankSetAccount({
        unlockToken, last4: f.summary.last4, kind: f.summary.kind, label,
        qboAccountId: picked.id, qboAccountName: picked.name,
      });
    } catch (err) {
      if (gen.current !== id) return;
      const msg = failure(err, SAVE_FAILED);
      if (msg) setFlow({ ...f, busy: null, error: msg });
      return;
    }
    changed(); // the mapping is in, whatever happens to the file
    if (gen.current !== id) return;
    // The server now finds the account by the file's last four digits.
    setFlow({ ...f, busy: "storing", error: null });
    try {
      const res = await upload(f.fileName, f.base64);
      if (gen.current === id) land(res, f.fileName, f.base64);
    } catch (err) {
      if (gen.current !== id) return;
      const msg = failure(err, UPLOAD_FAILED);
      if (msg) setFlow({ ...f, busy: null, error: msg });
    }
  };

  const storeAgainst = async () => {
    const f = flow;
    if (f.step !== "mapping" || f.busy || !f.accountId) return;
    const id = gen.current;
    setFlow({ ...f, busy: "storing", error: null });
    try {
      const res = await upload(f.fileName, f.base64, f.accountId);
      if (gen.current === id) land(res, f.fileName, f.base64);
    } catch (err) {
      if (gen.current !== id) return;
      const msg = failure(err, UPLOAD_FAILED);
      if (msg) setFlow({ ...f, busy: null, error: msg });
    }
  };

  const cancelFlow = () => {
    gen.current += 1;
    setFlow(IDLE); // drops the file
  };

  // ── Change (remap) ────────────────────────────────────────────────────────
  const editing = edit ? accounts.find((a) => a.accountId === edit.accountId) : null;
  let editChoices = [];
  if (editing) {
    const list = accountsForKind(qboAccts.data?.accounts, editing.kind);
    // The current mapping stays pickable even when QuickBooks no longer lists it (a label-only change).
    editChoices = list.some((a) => a.id === editing.qboAccountId) ? list :
      [{ id: editing.qboAccountId, name: editing.qboAccountName, current: true }, ...list];
  }
  const editPick = editChoices.find((a) => a.id === edit?.qboAccountId) ?? null;
  const remaps = Boolean(editing && editPick &&
    (editPick.id !== editing.qboAccountId || editPick.name !== editing.qboAccountName));

  const openChange = (a) => setEdit({
    accountId: a.accountId, label: a.label ?? "", qboAccountId: a.qboAccountId ?? "", busy: false, error: null,
  });

  const saveChange = async () => {
    const e = edit;
    const label = (e?.label ?? "").trim();
    if (!e || e.busy || !editing || !editPick || !label) return;
    setEdit({ ...e, busy: true, error: null });
    // Only this account's form takes the answer: another form opened meanwhile is left alone.
    const mine = (cur) => cur?.accountId === e.accountId;
    try {
      await bankSetAccount({
        unlockToken, last4: editing.last4, kind: editing.kind, label,
        qboAccountId: editPick.id, qboAccountName: editPick.name,
      });
      setEdit((cur) => (mine(cur) ? null : cur));
      changed();
    } catch (err) {
      const msg = failure(err, SAVE_FAILED);
      if (msg) setEdit((cur) => (mine(cur) ? { ...cur, busy: false, error: msg } : cur));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const choicesState = (list) => ({
    list, loading: qboAccts.loading && !qboAccts.data, error: qboAccts.error, onRetry: qboAccts.refresh,
  });

  let source;
  if (!connected) source = { v: "Read once QuickBooks is connected", tone: "dim" };
  else if (status.error) source = { v: "Could not be read (see below)", tone: "attn" };
  else if (!status.data) source = { v: "Loading…", tone: "dim" };
  else source = { v: sourceLine(status.data) };

  let mappedSection;
  if (!connected) {
    mappedSection = (
      <div style={{ padding: "10px 14px 12px", fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>
        Mapping a bank account or card to QuickBooks needs the QuickBooks connection.
      </div>
    );
  } else if (status.error) {
    mappedSection = (
      <div style={{ padding: "10px 14px 12px" }}>
        <FetchError what="Bank data" error={status.error} onRetry={status.refresh} />
      </div>
    );
  } else if (!status.data) {
    mappedSection = <div style={{ padding: "10px 14px 12px", fontSize: 12.5, color: C.muted }}>Loading bank data…</div>;
  } else {
    mappedSection = (
      <MappedAccounts
        accounts={accounts}
        onChange={openChange}
        disabled={(flow.step !== "idle" && flow.step !== "done") || Boolean(edit?.busy)}
        editId={edit?.accountId}
        editForm={editing ? (
          <RemapForm
            account={editing}
            label={edit.label}
            onLabel={(v) => setEdit((x) => ({ ...x, label: v }))}
            choices={choicesState(editChoices)}
            qboAccountId={edit.qboAccountId}
            onPick={(v) => setEdit((x) => ({ ...x, qboAccountId: v }))}
            remaps={remaps}
            busy={edit.busy}
            error={edit.error}
            onSave={saveChange}
            onCancel={() => setEdit(null)}
          />
        ) : null}
      />
    );
  }

  const busyLabel = flow.step === "reading" ? "Reading…" : flow.step === "uploading" ? "Storing…" : null;
  const uploadOff = !connected || Boolean(busyLabel) || flow.step === "mapping" || Boolean(edit?.busy);

  return (
    // As wide as the QuickBooks connection card above: its 520 plus its 18px padding each side.
    <div style={{ ...panel, padding: 0, maxWidth: 520 + 2 * 18, marginTop: 14 }}>
      <PanelHd title="Bank data" note="for the Bank vs QuickBooks check" />
      <Kv text rows={[
        { k: "Source", v: source.v, tone: source.tone },
        { k: "Format", v: FORMAT_LINE },
        { k: "Kept", v: KEPT_LINE },
      ]} />

      <div style={{ borderTop: `1px solid ${C.border}` }}>
        <div style={{ padding: "10px 14px 0" }}><span style={lblStyle}>Mapped accounts</span></div>
        {mappedSection}
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, padding: "12px 14px" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button type="button" disabled={uploadOff} onClick={() => inputRef.current?.click()}
            style={{ ...btnP, ...(uploadOff ? { opacity: 0.45, cursor: "default" } : null) }}>
            {busyLabel ?? "Upload statement"}
          </button>
          <input ref={inputRef} type="file" accept=".qbo,.qfx,.ofx,.csv" style={{ display: "none" }} onChange={onFile} />
          {!connected ? (
            <span style={{ fontSize: 12, color: C.muted }}>Needs the QuickBooks connection: each upload is compared with QuickBooks.</span>
          ) : null}
        </div>
        {flow.step === "idle" && flow.error ? (
          <div style={{ fontSize: 12.5, color: C.capture, marginTop: 8, lineHeight: 1.5 }}>{flow.error}</div>
        ) : null}
        {flow.step === "mapping" ? (
          <MappingPrompt
            reason={flow.reason}
            summary={flow.summary}
            fileName={flow.fileName}
            label={flow.label}
            onLabel={(v) => patch({ label: v })}
            choices={choicesState(qboChoices)}
            qboAccountId={flow.qboAccountId}
            onPickQbo={(v) => patch({ qboAccountId: v })}
            mapped={{
              list: mappedForFile(accounts, flow.summary), loading: status.loading && !status.data,
              error: status.error, onRetry: status.refresh,
            }}
            accountId={flow.accountId}
            onPickAccount={(v) => patch({ accountId: v })}
            busy={flow.busy}
            error={flow.error}
            onSave={saveAndStore}
            onStore={storeAgainst}
            onCancel={cancelFlow}
          />
        ) : null}
        {flow.step === "done" ? (
          <UploadResult
            result={flow.result}
            fileName={flow.fileName}
            accountName={nameOf(accounts, flow.result?.accountId)}
          />
        ) : null}
      </div>

      {connected && status.data ? (
        <RecentUploadsTable uploads={status.data.recentUploads} accounts={accounts} />
      ) : null}
      <PanelNote>
        A statement is compared against QuickBooks when it is uploaded. Re-uploading the same file is safe: nothing is
        stored twice.
      </PanelNote>
    </div>
  );
}

/** "{label} ··{last4}" for a bank_status accountId, or the id itself before the list has it. */
const nameOf = (accounts, accountId) => {
  const a = accounts.find((x) => x.accountId === accountId);
  return a ? acctName(a.label, a.last4) : accountId;
};
